import classNames from 'classnames';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowAngleRight } from '@zooplus/icons';
import { Anchor } from '@zooplus/ui-components';

import AppContext from '../../context';
import { FlyoutTypeEnum, UiCategory } from '../../types';
import {
    convertTrackingIdTitle,
    getTrackingId,
    isSpecialLink,
    resolveBannerItem,
} from '../../utils/categoryUtils';
import { ThemedIcon } from '../Icon/Icon';
import { LinkTypeEnum } from '../Link/Link';

import styles from './CategoryFlyout.module.scss';
import { FlyoutBanner } from './FlyoutBanner';
import { SpecialIcon } from './SpecialIcon';

const ICON_SIZE = 20;
/**
 * The cursor moves diagonally towards the right panel, so it passes over the
 * items below the one it started on. Waiting before switching means those
 * pass-overs never register.
 */
const LEFT_NAV_HOVER_DELAY = 300;

const hasFlyoutContent = (item: UiCategory): boolean =>
    (item.children?.length ?? 0) + (item.specials?.length ?? 0) > 0;

const getChildItems = (item: UiCategory): UiCategory[] =>
    [...(item.children ?? []), ...(item.specials ?? [])].filter(
        (child) => !!child.path,
    );

export function AnimalFlyoutV2({ category }: { category: UiCategory }) {
    const { translations, banner } = useContext(AppContext);
    const viewAllLabel = translations.get('shop.header.categories.title.link');
    const showAllLabel = translations.get(
        'shop.header.categories.all.products.link',
    );

    const { children = [], specials = [], title, path } = category;

    const leftItems = useMemo<UiCategory[]>(
        () => [...children, ...specials],
        [children, specials],
    );

    const [activeLeftIndex, setActiveLeftIndex] = useState(0);

    const categoryTrackingId = 'animal';
    const trackingIdTitle = convertTrackingIdTitle(title);

    const bannerItem = resolveBannerItem(
        category,
        FlyoutTypeEnum.ANIMAL,
        banner,
    );

    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelPendingHover = () => {
        if (hoverTimerRef.current !== null) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    };

    /** Hovering a left item switches the panel, but only after a short wait. */
    const scheduleItem = (index: number, item: UiCategory) => {
        if (!hasFlyoutContent(item)) {
            return;
        }

        cancelPendingHover();
        hoverTimerRef.current = setTimeout(
            () => setActiveLeftIndex(index),
            LEFT_NAV_HOVER_DELAY,
        );
    };

    /** Keyboard focus switches straight away — only hovering is delayed. */
    const activateItem = (index: number, item: UiCategory) => {
        if (!hasFlyoutContent(item)) {
            return;
        }

        cancelPendingHover();
        setActiveLeftIndex(index);
    };

    useEffect(() => cancelPendingHover, []);

    const renderGroup = (
        group: UiCategory,
        index: number,
        parentItem: UiCategory,
    ) => {
        const groupLinks = getChildItems(group);
        const showAll = group.hasMore === true;
        return (
            <ul
                id={getTrackingId({
                    parentTitle: parentItem.title ?? title,
                    type: group.type,
                    level: group.level,
                    index,
                    title: group.title,
                })}
                className={styles.animalPanelGroup}
                key={group.path}>
                <li className={styles.groupHeader}>
                    <Anchor
                        variant={LinkTypeEnum.NEUTRAL}
                        data-link-type={LinkTypeEnum.NEUTRAL}
                        data-zta="category-flyout-animal-group-header"
                        className={styles.groupHeaderText}
                        title={group.title}
                        href={group.path}>
                        {group.title}
                    </Anchor>
                </li>
                {groupLinks.map((link) => {
                    const isSpecial = isSpecialLink(link);
                    return (
                        <li key={link.path} className={styles.groupItem}>
                            <Anchor
                                href={link.path}
                                variant={LinkTypeEnum.NEUTRAL}
                                data-link-type={LinkTypeEnum.NEUTRAL}
                                data-zta="category-flyout-animal-level4-link"
                                aria-label="group-item"
                                title={link.title}
                                iconLeft={
                                    isSpecial
                                        ? () => <SpecialIcon spaced />
                                        : undefined
                                }>
                                {link.title}
                            </Anchor>
                        </li>
                    );
                })}
                {showAll && (
                    <li className={styles.groupFooter}>
                        <Anchor
                            href={group.path}
                            variant={LinkTypeEnum.PRIMARY}
                            data-link-type={LinkTypeEnum.PRIMARY}
                            data-zta="category-flyout-animal-group-footer">
                            {showAllLabel}
                        </Anchor>
                    </li>
                )}
            </ul>
        );
    };

    return (
        <div
            className={styles.animalflyoutV2}
            data-zta={`category-flyout-v2-${categoryTrackingId}-${trackingIdTitle}`}>
            <div className={styles.animalPanelV2Layout}>
                <ul
                    className={styles.leftNav}
                    onMouseLeave={cancelPendingHover}
                    data-zta="category-flyout-animal-left-nav">
                    {leftItems.map((item, index) => {
                        const isActive = index === activeLeftIndex;
                        const isSpecial = isSpecialLink(item);
                        const hasContent = hasFlyoutContent(item);
                        return (
                            <li
                                key={item.path}
                                className={classNames({
                                    [styles.leftNavItemActive]: isActive,
                                })}>
                                <Anchor
                                    href={item.path}
                                    variant={LinkTypeEnum.NEUTRAL}
                                    isActive={isActive}
                                    isBold={isActive}
                                    data-link-type={LinkTypeEnum.NEUTRAL}
                                    data-zta="category-flyout-animal-left-nav-link"
                                    className={styles.leftNavLink}
                                    title={item.title}
                                    onMouseEnter={() =>
                                        scheduleItem(index, item)
                                    }
                                    onFocus={() => activateItem(index, item)}
                                    iconLeft={
                                        isSpecial
                                            ? () => <SpecialIcon />
                                            : undefined
                                    }
                                    iconRight={
                                        hasContent
                                            ? () => (
                                                  <ThemedIcon
                                                      color="neutralDark"
                                                      aligned
                                                      size={ICON_SIZE}
                                                      className={
                                                          styles.leftNavChevron
                                                      }
                                                      Icon={ArrowAngleRight}
                                                      aria-hidden="true"
                                                  />
                                              )
                                            : undefined
                                    }>
                                    {item.title}
                                </Anchor>
                            </li>
                        );
                    })}
                </ul>

                <div
                    className={styles.animalPanelV2RightArea}
                    data-zta="category-flyout-animal-content">
                    <div className={styles.flyOutHeader}>
                        <Anchor
                            href={path}
                            isBold
                            variant={LinkTypeEnum.PRIMARY}
                            data-link-type={LinkTypeEnum.PRIMARY}
                            data-zta={`category-flyout-${trackingIdTitle}-level2-link`}>
                            {viewAllLabel} {title}
                        </Anchor>
                    </div>
                    {leftItems.map((item, index) => {
                        const isActive = index === activeLeftIndex;
                        return (
                            <div
                                key={item.path}
                                className={classNames(styles.animalPanelV2, {
                                    [styles.animalPanelHidden]: !isActive,
                                })}
                                data-zta="category-flyout-animal-columns"
                                aria-hidden={!isActive}>
                                {getChildItems(item).map((group, groupIndex) =>
                                    renderGroup(group, groupIndex, item),
                                )}
                            </div>
                        );
                    })}

                    <div className={styles.animalPanelV2BannerColumn}>
                        <FlyoutBanner
                            banner={bannerItem}
                            animalType={category.animalType}
                            categoryType={category.type}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
